import { useState, useMemo, useEffect, useCallback } from "react";
import {
  Search, SlidersHorizontal, Plus, Pencil, Trash2,
  X, Check, AlertTriangle, ChevronDown,
  Loader2, CreditCard,
} from "lucide-react";
import { usePaymentTypes } from "../context/PaymentTypeContext";
import Pagination, { usePagination } from '../shared/Pagination';

// ─── Delete Modal ────────────────────────────────────────────────────────────

function DeleteModal({ isOpen, deleteItem, onClose, onConfirm, submitting }) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (isOpen) setTimeout(() => setVisible(true), 10);
    else setVisible(false);
  }, [isOpen]);

  if (!isOpen || !deleteItem) return null;

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center px-4 transition-all duration-300 ${visible ? "bg-black/40 backdrop-blur-sm" : "bg-black/0"}`}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className={`bg-white rounded-3xl shadow-2xl w-full max-w-sm overflow-hidden transition-all duration-300 ${visible ? "opacity-100 scale-100 translate-y-0" : "opacity-0 scale-95 translate-y-4"}`}>
        <div className="h-1.5 w-full bg-red-500" />
        <div className="p-6 text-center">
          <div className="w-16 h-16 bg-red-50 border-2 border-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Trash2 size={24} className="text-red-500" />
          </div>
          <h2 className="text-lg font-bold text-[#1a2e2b] mb-1">Delete Order Type?</h2>
          <p className="text-sm text-gray-500 mb-4 leading-relaxed">
            You're about to delete <span className="font-semibold text-[#1a2e2b]">{deleteItem.type}</span>. This action cannot be undone.
          </p>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 py-3 rounded-2xl border border-[#e8e0d0] text-sm text-gray-500 hover:bg-gray-50 font-medium transition-all"
            >
              Cancel
            </button>
            <button
              onClick={() => onConfirm(deleteItem.id)}
              disabled={submitting}
              className="flex-1 py-3 rounded-2xl bg-red-500 hover:bg-red-600 disabled:opacity-60 text-white text-sm font-bold shadow-lg shadow-red-200 transition-all active:scale-95"
            >
              <span className="flex items-center justify-center gap-2">
                {submitting ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                Delete
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Toggle ──────────────────────────────────────────────────────────────────

function Toggle({ active, onToggle, disabled }) {
  return (
    <button
      onClick={onToggle}
      disabled={disabled}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-teal-400 disabled:opacity-50 disabled:cursor-not-allowed ${active ? "bg-teal-600" : "bg-gray-200"}`}
    >
      <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-md transition-transform duration-300 ${active ? "translate-x-6" : "translate-x-1"}`} />
    </button>
  );
}

// ─── Toast ───────────────────────────────────────────────────────────────────

function Toast({ toast }) {
  if (!toast) return null;
  const isError = toast.type === "error";
  return (
    <div className={`fixed bottom-6 right-6 z-[60] flex items-center gap-3 px-4 py-3 rounded-2xl shadow-xl text-sm font-semibold transition-all duration-300 ${isError ? "bg-red-500 text-white" : "bg-teal-600 text-white"}`}>
      {isError ? <AlertTriangle size={15} /> : <Check size={15} />}
      {toast.message}
    </div>
  );
}

// ─── Main Page ───────────────────────────────────────────────────────────────

export default function OrderTypesPage() {
  const {
    paymentTypes,
    paymentTypesLoading,
    fetchPaymentTypes,
    addPaymentType,
    updatePaymentType,
    deletePaymentType,
    toggleStatus,
  } = usePaymentTypes();

  const [search, setSearch] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortBy, setSortBy] = useState("name-asc");

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);

  const [submitting, setSubmitting] = useState(false);
  const [toggling, setToggling] = useState(null);

  const [toast, setToast] = useState(null);
  const showToast = useCallback((message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  const activeCount = paymentTypes.filter((p) => p.status === "Active").length;
  const inactiveCount = paymentTypes.length - activeCount;

  const filtered = useMemo(() => {
    let result = [...paymentTypes];
    if (search) result = result.filter((p) => p.type?.toLowerCase().includes(search.toLowerCase()));
    if (statusFilter === "active") result = result.filter((p) => p.status === "Active");
    if (statusFilter === "inactive") result = result.filter((p) => p.status !== "Active");
    result.sort((a, b) => {
      if (sortBy === "name-asc") return (a.type || "").localeCompare(b.type || "");
      if (sortBy === "name-desc") return (b.type || "").localeCompare(a.type || "");
      return 0;
    });
    return result;
  }, [paymentTypes, search, statusFilter, sortBy]);

  const { page: ptPage, pageSize: ptSize, setPage: setPtPage, setPageSize: setPtSize, totalPages: ptTotalPages, totalItems: ptTotal, paginated: paginatedTypes } = usePagination(filtered, 10);

  const handleToggle = async (id, currentStatus) => {
    setToggling(id);
    try {
      await toggleStatus(id, currentStatus);
      showToast("Status updated successfully");
    } catch {
      showToast("Failed to update status", "error");
    } finally {
      setToggling(null);
    }
  };

  const handleDeletePrompt = (item) => { setDeleteItem(item); setDeleteModalOpen(true); };

  const handleDeleteConfirm = async (id) => {
    setSubmitting(true);
    try {
      await deletePaymentType(id);
      showToast("Order type deleted");
      setDeleteModalOpen(false);
      setDeleteItem(null);
    } catch {
      showToast("Failed to delete order type", "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 font-sans">
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6">

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3 mb-5">
          {[
            { label:"Total Types", value:paymentTypes.length, accent:'border-l-teal-500',  color:'text-teal-700'  },
            { label:"Active",      value:activeCount,          accent:'border-l-green-500', color:'text-green-600' },
            { label:"Inactive",    value:inactiveCount,        accent:'border-l-red-400',   color:'text-red-500'   },
          ].map(({ label, value, accent, color }) => (
            <div key={label} className={`bg-white rounded-xl border border-gray-100 border-l-4 ${accent} p-4 flex items-center gap-3 shadow-sm`}>
              <div><p className={`text-xl font-bold ${color}`}>{value}</p><p className="text-xs text-gray-400 leading-tight">{label}</p></div>
            </div>
          ))}
        </div>

        {/* Search + Filters */}
        <div className="bg-white border border-[#e8e0d0] rounded-2xl shadow-sm p-3 mb-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search order types..."
                className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-[#e8e0d0] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-teal-300 focus:bg-white transition-all text-[#1a2e2b] placeholder-gray-400"
              />
            </div>
            <button
              onClick={() => setFiltersOpen(!filtersOpen)}
              className={`flex items-center gap-2 px-4 py-2.5 border rounded-xl text-sm font-semibold transition-all whitespace-nowrap ${filtersOpen ? "border-teal-500 bg-teal-600 text-white" : "border-[#e8e0d0] bg-gray-50 hover:bg-gray-100 text-[#1a2e2b]"}`}
            >
              <SlidersHorizontal size={14} />
              Filters
              <ChevronDown size={12} className={`transition-transform duration-200 ${filtersOpen ? "rotate-180" : ""}`} />
            </button>
          </div>

          {filtersOpen && (
            <div className="mt-4 pt-4 border-t border-[#e8e0d0] grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-[#1a2e2b] mb-2">Status</label>
                <div className="relative">
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="w-full appearance-none bg-white border border-[#e8e0d0] rounded-xl px-4 py-2.5 text-sm text-[#1a2e2b] focus:outline-none focus:ring-2 focus:ring-teal-300 cursor-pointer pr-9"
                  >
                    <option value="all">All Types ({paymentTypes.length})</option>
                    <option value="active">Active ({activeCount})</option>
                    <option value="inactive">Inactive ({inactiveCount})</option>
                  </select>
                  <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#1a2e2b] mb-2">Sort By</label>
                <div className="relative">
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="w-full appearance-none bg-white border border-[#e8e0d0] rounded-xl px-4 py-2.5 text-sm text-[#1a2e2b] focus:outline-none focus:ring-2 focus:ring-teal-300 cursor-pointer pr-9"
                  >
                    <option value="name-asc">Name A–Z</option>
                    <option value="name-desc">Name Z–A</option>
                  </select>
                  <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Count */}
        <div className="flex items-center justify-between mb-3 px-1">
          <p className="text-sm text-[#8a7a5a]">
            Showing <span className="font-bold text-[#1a2e2b]">{filtered.length}</span> of{" "}
            <span className="font-bold text-[#1a2e2b]">{paymentTypes.length}</span> Order Types
          </p>
        </div>

        {/* Table */}
        {paymentTypesLoading && paymentTypes.length === 0 ? (
          <div className="bg-white rounded-2xl border border-[#e8e0d0] py-16 flex flex-col items-center justify-center text-gray-400">
            <Loader2 size={32} className="mb-3 animate-spin text-teal-400" />
            <p className="text-sm font-medium">Loading order types…</p>
          </div>
        ) : (
          <div className="bg-white border border-[#e8e0d0] rounded-2xl overflow-hidden shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[400px]">
                <thead>
                  <tr className="bg-teal-700 text-white">
                    {["ID", "Order Type", "Status", "Actions"].map((h, i) => (
                      <th
                        key={h}
                        className={`px-5 py-3.5 text-xs font-semibold uppercase tracking-wider ${i === 3 ? "text-right" : "text-left"}`}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#f0ebe0]">
                  {filtered.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="px-5 py-12 text-center text-sm text-gray-400">
                        No order types found.
                      </td>
                    </tr>
                  ) : (
                    paginatedTypes.map((item, index) => {
                      const isActive = item.status === "Active";
                      return (
                        <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-5 py-3.5 text-sm text-[#8a7a5a] font-medium w-12">
                            {index + 1}
                          </td>
                          <td className="px-5 py-3.5">
                            <span className="font-medium text-[#1a2e2b] text-sm">{item.type}</span>
                          </td>
                          <td className="px-5 py-3.5">
                            <div className="flex items-center gap-2">
                              <Toggle
                                active={isActive}
                                onToggle={() => handleToggle(item.id, item.status)}
                                disabled={toggling === item.id}
                              />
                              {toggling === item.id && (
                                <Loader2 size={13} className="animate-spin text-teal-500" />
                              )}
                              <span className={`text-xs font-medium ${isActive ? "text-teal-600" : "text-gray-400"}`}>
                                {isActive ? "Active" : "Inactive"}
                              </span>
                            </div>
                          </td>
                          <td className="px-5 py-3.5 text-right">
                            <button
                              onClick={() => handleDeletePrompt(item)}
                              className="p-2 rounded-lg text-red-400 hover:text-red-500 hover:bg-red-50 transition-all"
                            >
                              <Trash2 size={14} />
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
              <div className="px-4 border-t border-gray-100">
                <Pagination currentPage={ptPage} totalPages={ptTotalPages} totalItems={ptTotal}
                  pageSize={ptSize} onPageChange={setPtPage} onPageSizeChange={setPtSize} />
              </div>
            </div>
          </div>
        )}

      </main>

      <DeleteModal
        isOpen={deleteModalOpen}
        deleteItem={deleteItem}
        onClose={() => { setDeleteModalOpen(false); setDeleteItem(null); }}
        onConfirm={handleDeleteConfirm}
        submitting={submitting}
      />
      <Toast toast={toast} />
    </div>
  );
}